import Vue from "vue"
import VueAnalytics from "vue-analytics"
import Router from "vue-router"
import Meta from "vue-meta"
import {
  requiresAuth,
  requiresAnon,
  requiresPermission,
  requiresError
} from "@/middleware/router-guards"
import { ChapterPermissions } from "@/utils/enums/security-enums"
import FullContainer from "@/containers/full-container"

export const NAV_GROUPS = {
  GLOBAL: "global",
  MERCHANDISING: "merchandising",
  PROMOTION: "promotion",
  DEVELOPMENT: "development",
  SETTINGS: "settings"
}

export const APP_ROUTES = [
  {
    path: "/",
    name: "Base",
    component: FullContainer,
    data: {},
    redirect: "/overview",
    children: [
      {
        path: "/overview",
        name: "Overview",
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport("overview"),
        meta: {
          icon: "mdi-view-dashboard",
          permissions: [ChapterPermissions.PdmAccess],
          group: NAV_GROUPS.GLOBAL
        }
      },
      {
        path: "/orders",
        name: "Orders",
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport("orders/index"),
        meta: {
          icon: "mdi-cart",
          permissions: [ChapterPermissions.CanViewOrders],
          group: NAV_GROUPS.GLOBAL
        },
        children: [
          {
            path: "pick-pack-list",
            name: "Pick/Pack List Multiple",
            component: componentImport("orders/pick-pack-list"),
            meta: {
              permissions: [ChapterPermissions.CanViewOrders]
            }
          }
        ]
      },
      {
        path: "/orders/:number",
        name: "Order Detail",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("orders/detail"),
        meta: {
          icon: "mdi-book-open",
          permissions: [ChapterPermissions.CanViewOrders]
        },
        children: [
          {
            path: "pick-pack-list",
            name: "Pick/Pack List Single",
            component: componentImport("orders/pick-pack-list"),
            meta: {
              permissions: [ChapterPermissions.CanViewOrders]
            }
          }
        ]
      },
      {
        path: "/products",
        name: "Products",
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport("products/index"),
        meta: {
          icon: "mdi-book-open",
          permissions: [ChapterPermissions.CanViewProducts],
          group: NAV_GROUPS.MERCHANDISING
        }
      },
      {
        path: "/authors",
        name: "Authors",
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport("authors/index"),
        meta: {
          icon: "mdi-account",
          permissions: [ChapterPermissions.CanViewProducts],
          group: NAV_GROUPS.MERCHANDISING
        }
      },
      {
        path: "/giftcards",
        name: "Gift Cards",
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport("giftcards/list"),
        meta: {
          icon: "mdi-credit-card",
          permissions: [ChapterPermissions.CanEditProducts],
          group: NAV_GROUPS.MERCHANDISING
        }
      },
      {
        path: "/giftcards/giftcards/giftcard-product-image-update",
        name: "GiftCards",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("giftcards/giftcard-product-image-update"),
        meta: {
          icon: "mdi-credit-card",
          permissions: [ChapterPermissions.CanEditProducts]
        }
      },
      {
        path: "/giftcards/:id",
        name: "GiftCardsEdit",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("giftcards/giftcard"),
        meta: {
          icon: "mdi-book-open",
          permissions: [ChapterPermissions.CanEditProducts]
        }
      },
      {
        path: "/products/:id/edit",
        name: "ProductsEdit",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("products/edit"),
        meta: {
          icon: "mdi-book-open",
          permissions: [ChapterPermissions.CanViewProducts]
        }
      },
      {
        path: "/authors/:id/edit",
        name: "AuthorsEdit",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("authors/edit"),
        meta: {
          icon: "mdi-account",
          permissions: [ChapterPermissions.CanViewProducts]
        }
      },
      {
        path: "/categories",
        name: "Categories",
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport("categories/index"),
        meta: {
          icon: "mdi-file-tree",
          permissions: [ChapterPermissions.CanViewProducts],
          group: NAV_GROUPS.MERCHANDISING
        }
      },
      {
        path: "/categories/:id/edit",
        name: "CategoriesEdit",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("categories/category-edit"),
        meta: {
          permissions: [ChapterPermissions.CanEditProducts]
        }
      },
      {
        path: "/categories/bisaccodes/:id/edit",
        name: "CategoriesBisacCodeEdit",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("categories/bisac-code-edit"),
        meta: {
          permissions: [ChapterPermissions.CanEditProducts]
        }
      },
      {
        path: "/categories/bsmCategories/:id/edit",
        name: "BsmCategoriesEdit",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("categories/bsm-category-edit"),
        meta: {
          permissions: [ChapterPermissions.CanEditProducts]
        }
      },
      {
        path: "/formats",
        name: "Formats",
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport("formats/index"),
        meta: {
          icon: "mdi-notebook",
          permissions: [ChapterPermissions.CanViewProducts],
          group: NAV_GROUPS.MERCHANDISING
        }
      },
      {
        path: "/bindings/:id/edit",
        name: "BindingsEdit",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("formats/binding-edit"),
        meta: {
          permissions: [ChapterPermissions.CanEditProducts]
        }
      },
      {
        path: "/imprints",
        name: "Imprints",
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport("imprints/index"),
        meta: {
          icon: "mdi-alpha-i-box",
          permissions: [ChapterPermissions.CanViewProducts],
          group: NAV_GROUPS.GLOBAL
        }
      },
      {
        path: "/imprints/:id/edit",
        name: "ImprintsEdit",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("imprints/edit"),
        meta: {
          permissions: [ChapterPermissions.CanViewProducts]
        }
      },

      // {
      //   path: '/reviews',
      //   name: 'Reviews',
      //   nav: true,
      //   beforeEnter: requiresAuth,
      //   component: componentImport('reviews'),
      //   meta: {
      //     icon: 'mdi-comment-multiple'
      //   }
      // },
      {
        path: "/publisher-discounts",
        name: "Publisher Discounts",
        nav: true,
        beforeEnter: requiresAuth,
        component: componentImport("publisher discounts/index"),
        meta: {
          icon: "mdi-animation",
          permissions: [ChapterPermissions.CanViewProducts],
          group: NAV_GROUPS.PROMOTION
        }
      },
      {
        path: "/publisher-discounts/create",
        name: "Publisher Discounts Create",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("publisher discounts/create"),
        meta: {
          icon: "mdi-tag",
          permissions: [ChapterPermissions.CanViewProducts]
        }
      },
      {
        path: "/publisher-discounts/:imprintName/:discountCode/edit",
        name: "Publisher Discounts Edit",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("publisher discounts/edit"),
        meta: {
          icon: "mdi-tag",
          permissions: [ChapterPermissions.CanViewProducts]
        }
      },
      {
        path: "/publisher-discounts/:imprintName/edit",
        name: "Publisher Discounts Edit No Code",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("publisher discounts/edit"),
        meta: {
          icon: "mdi-tag",
          permissions: [ChapterPermissions.CanViewProducts]
        }
      },
      {
        path: "/promotions",
        name: "Promotions",
        nav: true,
        beforeEnter: requiresAuth,
        component: componentImport("promotions/index"),
        meta: {
          icon: "mdi-tag",
          permissions: [ChapterPermissions.CanViewPromotions],
          group: NAV_GROUPS.PROMOTION
        }
      },
      {
        path: "/promotions/create",
        name: "PromotionsCreate",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("promotions/edit"),
        meta: {
          permissions: [ChapterPermissions.CanEditPromotions]
        }
      },
      {
        path: "/promotions/:id",
        name: "PromotionsEdit",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("promotions/edit"),
        meta: {
          permissions: [ChapterPermissions.CanEditPromotions]
        }
      },
      {
        path: "/customers",
        name: "Customers",
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport("customers/index"),
        meta: {
          icon: "mdi-account-multiple",
          permissions: [ChapterPermissions.CanViewCustomers],
          group: NAV_GROUPS.GLOBAL
        }
      },
      {
        path: "/customers/:id/detail",
        name: "Customer Detail",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("customers/detail"),
        meta: {
          permissions: [ChapterPermissions.CanViewCustomers]
        }
      },
      {
        path: "/customers/:id/edit",
        name: "Customer Edit",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("customers/edit"),
        meta: {
          permissions: [ChapterPermissions.CanEditCustomers]
        }
      },
      {
        path: "/organizations",
        name: "Organizations",
        nav: true,
        beforeEnter: requiresAuth,
        component: componentImport("organizations/index"),
        meta: {
          icon: "mdi-church",
          permissions: [ChapterPermissions.CanEditOrganizationUsers],
          tenants: ['Baker Book House'],
          group: NAV_GROUPS.GLOBAL
        }
      },
      {
        path: "/organizations/create",
        name: "Organizations Create",
        nav: false,
        beforeEnter: requiresAuth,
        component: componentImport("organizations/edit"),
        meta: {
          permissions: [ChapterPermissions.CanEditOrganizationUsers]
        }
      },
      {
        path: "/organizations/:id/edit",
        name: "Organization Edit",
        nav: false,
        beforeEnter: requiresAuth,
        component: componentImport("organizations/edit"),
        meta: {
          permissions: [ChapterPermissions.CanEditOrganizationUsers]
        }
      },
      {
        path: "/data-discrepancies",
        name: "Data Discrepancies",
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport("data-discrepancies"),
        meta: {
          icon: "mdi-database",
          permissions: [
            ChapterPermissions.CanViewNotifications,
            ChapterPermissions.CanMergeRelatedProductData
          ],
          group: NAV_GROUPS.DEVELOPMENT
        }
      },
      {
        path: "/reports",
        name: "Reports",
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport("reports/index"),
        meta: {
          icon: "mdi-poll",
          permissions: [ChapterPermissions.CanViewReports],
          navChildren: [
            { name: "Best Sellers Report", path: "/reports/best-sellers" },
            { name: "Sales Tax Report", path: "/reports/sales-tax" },
            { name: "Sales Detail Report", path: "/reports/sales-detail" },
            { name: "Sales Overview Report", path: "/reports/sales-overview" },
            { name: "Promotions Report", path: "/reports/promotions" },
            { name: "Gift Cards Report", path: "/reports/giftcards" },
            { name: "Areas of Interest", path: "/reports/areas-of-interest" }
          ],
          group: NAV_GROUPS.GLOBAL
        }
      },
      {
        path: "/exam-copy-requests",
        name: "Exam Copy Requests",
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport("exam-copy-requests/index"),
        meta: {
          icon: "mdi-book-open-outline",
          // TODO: what permissions does this require?
          permissions: [ChapterPermissions.CanViewProducts],
          group: NAV_GROUPS.GLOBAL,
          tenants: ['Baker Academic']
        }
      },
      {
        path: "/exam-copy-requests/:id",
        name: "Exam Copy Request Details",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport(
          "exam-copy-requests/exam-copy-request-details"
        ),
        meta: {
          permissions: [ChapterPermissions.CanViewProducts]
        }
      },
      {
        path: "/verification-requests",
        name: "Verification Requests",
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport("verification-requests/index"),
        meta: {
          icon: "mdi-account-voice",
          permissions: [ChapterPermissions.CanViewProducts],
          tenants: ['Baker Academic'],
          group: NAV_GROUPS.GLOBAL
        }
      },
      {
        path: "/verification-requests/:id",
        name: "Verification Request Details",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport(
          "verification-requests/verification-request-details"
        ),
        meta: {
          permissions: [ChapterPermissions.CanViewProducts]
        }
      },
      {
        path: "/reports/best-sellers",
        name: "Best Sellers Report",
        beforeEnter: requiresPermission,
        component: componentImport("reports/best-sellers"),
        meta: {
          permissions: [ChapterPermissions.CanViewReports]
        }
      },
      {
        path: "/reports/areas-of-interest",
        name: "Areas of Interest Report",
        beforeEnter: requiresPermission,
        component: componentImport("reports/areas-of-interest"),
        meta: {
          permissions: [ChapterPermissions.CanViewReports]
        }
      },
      {
        path: "/reports/sales-tax",
        name: "Sales Tax Report",
        beforeEnter: requiresPermission,
        component: componentImport("reports/sales-tax"),
        meta: {
          permissions: [ChapterPermissions.CanViewReports]
        }
      },
      {
        path: "/reports/sales-detail",
        name: "Sales Detail Report",
        beforeEnter: requiresPermission,
        component: componentImport("reports/sales-detail"),
        meta: {
          permissions: [ChapterPermissions.CanViewReports]
        }
      },
      {
        path: "/reports/sales-overview",
        name: "Sales Overview Report",
        beforeEnter: requiresPermission,
        component: componentImport("reports/sales-overview"),
        meta: {
          permissions: [ChapterPermissions.CanViewReports]
        }
      },
      {
        path: "/reports/promotions",
        name: "Promotions Report",
        beforeEnter: requiresPermission,
        component: componentImport("reports/promotions"),
        meta: {
          permissions: [ChapterPermissions.CanViewReports]
        }
      },
      {
        path: "/reports/giftcards",
        name: "Gift Cards Report",
        beforeEnter: requiresPermission,
        component: componentImport("giftcards/giftcard-report"),
        meta: {
          permissions: [ChapterPermissions.CanViewReports]
        }
      },
      {
        path: "/notifications",
        name: "Notifications",
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport("notifications/index"),
        meta: {
          icon: "mdi-bell",
          permissions: [ChapterPermissions.CanViewNotifications],
          group: NAV_GROUPS.DEVELOPMENT
        }
      },
      {
        path: "/adf-pipelines",
        name: "ADF Pipelines",
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport("adf-pipelines/index"),
        meta: {
          icon: "mdi-pipe",
          permissions: [ChapterPermissions.CanViewAzureData],
          group: NAV_GROUPS.DEVELOPMENT
        }
      },
      {
        path: "/flags",
        name: "Flags",
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport("flags/index"),
        meta: {
          icon: "mdi-tag",
          permissions: [ChapterPermissions.CanEditFlags],
          group: NAV_GROUPS.MERCHANDISING
        }
      },
      {
        path: "/flags/create",
        name: "Flags Create",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("flags/create"),
        meta: {
          permissions: [ChapterPermissions.CanEditFlags]
        }
      },
      {
        path: "/flags/:id/edit",
        name: "Flag Edit",
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport("flags/create"),
        meta: {
          permissions: [ChapterPermissions.CanEditFlags]
        }
      },
      {
        path: '/tenants',
        name: 'Tenants',
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport('tenants/index'),
        meta: {
          icon: 'mdi-home-city',
          permissions: [
            ChapterPermissions.CanEditTenants
          ],
          group: NAV_GROUPS.SETTINGS
        }
      },
      {
        path: '/tenants/create',
        name: 'Tenants Create',
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport('tenants/edit'),
        meta: {
          permissions: [
            ChapterPermissions.CanEditTenants
          ] }
      },
      {
        path: '/tenants/:id/edit',
        name: 'Tenant Edit',
        nav: false,
        beforeEnter: requiresPermission,
        component: componentImport('tenants/edit'),
        meta: {
          permissions: [
            ChapterPermissions.CanEditTenants
          ]
        }
      },
      {
        path: "/settings",
        name: "Settings",
        nav: true,
        beforeEnter: requiresPermission,
        component: componentImport("settings/index"),
        meta: {
          icon: "mdi-cog-box",
          permissions: [
            ChapterPermissions.CanViewUsers,
            ChapterPermissions.CanViewRoles
          ],
          group: NAV_GROUPS.SETTINGS
        }
      },
      {
        path: "/error",
        name: "Error",
        component: componentImport("error"),
        meta: {},
        beforeEnter: requiresError
      },
      {
        path: "/auth/callback",
        name: "Callback",
        component: componentImport("auth/callback"),
        meta: {}
      },
      {
        path: "/auth/signed-off",
        name: "SignedOff",
        beforeEnter: requiresAnon,
        component: componentImport("auth/signed-off"),
        meta: {}
      },
      {
        path: "/auth/unauthorized",
        name: "Unauthorized",
        component: componentImport("auth/unauthorized"),
        meta: {}
      },
      {
        path: "/pdfs",
        name: "Archived Order PDFs",
        component: componentImport("pdfs/index"),
        nav: true,
        beforeEnter: requiresPermission,
        meta: {
          icon: "mdi-download",
          permissions: [ChapterPermissions.CanViewOrders],
          group: NAV_GROUPS.GLOBAL
        }
      }
    ]
  }
]

function componentImport (fileName) {
  return (resolve) => import(
      `@/views/${fileName}.vue`
  ).then(resolve)
}

Vue.use(Router)

// Create a new router
const router = new Router({
  mode: "history",
  routes: APP_ROUTES,
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { selector: to.hash }
    }
    return { x: 0, y: 0 }
  }
})

Vue.use(Meta)

// Bootstrap Analytics
// Set in .env
// https://github.com/MatteoGabriele/vue-analytics
if (process.env.VUE_APP_GOOGLE_ANALYTICS) {
  Vue.use(VueAnalytics, {
    id: process.env.VUE_APP_GOOGLE_ANALYTICS,
    router,
    autoTracking: {
      page: process.env.NODE_ENV !== "development"
    }
  })
}

export default router
